import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { LineItemType } from '@lambda/apis/src/cart/types';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import {
  getAccessoriesPath,
  getLastAdded,
  isPurchaseFlowEnabled,
  LastAddedT,
} from '@/lib/purchase-flow';
import {
  getCartLineSkuId,
  isAccessoryLine,
} from '@/components/v2/cart/helpers';
import { PURCHASE_FLOW_STEPS, PurchaseFlowStep } from './PurchaseFlowStepper';

const STEP_BY_ROUTE: Record<string, PurchaseFlowStep> = {
  '/cart': 'cart',
  '/checkout/[checkoutId]': 'checkout',
};

const usePurchaseFlowNav = (): {
  current: PurchaseFlowStep;
  steps: PurchaseFlowStep[];
  links: Partial<Record<PurchaseFlowStep, string>>;
} | null => {
  const { pathname } = useRouter();
  const { customer } = useCustomerContext();
  const { cartLines } = useCartContext();

  // Read after mount: storage doesn't exist on the server, and reading it
  // during render would make the bar differ between server and client.
  const [lastAdded, setLastAdded] = useState<LastAddedT | null>(null);

  useEffect(() => {
    setLastAdded(getLastAdded());
  }, []);

  const { skuId, accessoriesSkipped } = useMemo(() => {
    const deviceSkuIds = cartLines
      .filter(
        (line) => line.type === LineItemType.PRODUCT && !isAccessoryLine(line),
      )
      .map(getCartLineSkuId)
      .filter((id): id is string => Boolean(id));

    if (lastAdded && deviceSkuIds.includes(lastAdded.skuId)) {
      return lastAdded;
    }

    return {
      skuId: deviceSkuIds[deviceSkuIds.length - 1],
      accessoriesSkipped: false,
    };
  }, [cartLines, lastAdded]);

  const current = STEP_BY_ROUTE[pathname];

  if (!current || !isPurchaseFlowEnabled(customer?.email)) {
    return null;
  }

  // A device whose accessories page had nothing to show never went through
  // that step, so the bar leaves it out.
  if (accessoriesSkipped) {
    return {
      current,
      steps: PURCHASE_FLOW_STEPS.filter((step) => step !== 'accessories'),
      links: { cart: '/cart' },
    };
  }

  return {
    current,
    steps: PURCHASE_FLOW_STEPS,
    links: {
      accessories: skuId ? getAccessoriesPath(skuId) : undefined,
      cart: '/cart',
    },
  };
};

export default usePurchaseFlowNav;
